import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
    ChangeDetectionStrategy, Component, Input
} from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import {
    MatList, MatListItem
} from "@angular/material/list";

import { ExpandableFormSectionComponent } from "../expandable-form-section/expandable-form-section.component";

/**
 * The "N records to be updated" expander of a Bulk Update Form — a collapsed
 * `mgd-expandable-form-section` whose header counts the selection and whose body lists each
 * record through `displayWith`. Display only: it reads nothing from and writes nothing to the
 * form. Place it after the `<form>`, inside `mgd-dialog-content-container`.
 *
 * @example
 * ```html
 * <mgd-bulk-update-selection [records]="stores" [displayWith]="storeLabel"
 *                            recordLabel="store" recordLabelPlural="stores" />
 * ```
 */
@Component({
    selector: "mgd-bulk-update-selection",
    templateUrl: "./bulk-update-selection.component.html",
    styleUrl: "./bulk-update-selection.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ExpandableFormSectionComponent,
        MatIcon,
        MatList,
        MatListItem
    ]
})
export class BulkUpdateSelectionComponent<T = unknown> {

    /** The selection the bulk form will update. */
    @Input({ required: true }) records: ReadonlyArray<T> = [];

    /** The row label for one record. */
    @Input({ required: true }) displayWith: (record: T) => string;

    /** Singular noun for the header, e.g. `store`. */
    @Input() recordLabel = "record";

    /** Plural noun for the header, e.g. `stores`. */
    @Input() recordLabelPlural = "records";

    /** Whether the list starts expanded. */
    @Input({ transform: coerceBooleanProperty }) expanded = false;

    /** `2 stores to be updated` / `1 store to be updated`. */
    get heading(): string {

        const count = this.records?.length ?? 0;
        return `${count} ${count === 1 ? this.recordLabel : this.recordLabelPlural} to be updated`;
    }
}
